<template>
  <div
    v-if="accordions"
    class="accordion"
    :class="[
      $blockClass(blockClass, 'wrapper'),
    ]"
  >
    <div
      v-for="(accordion, index) of accordions"
      :key="index"
      class="accordion__item"
      :class="[
        $blockClass(blockClass, 'accordion'),
        { [$class('active')]: isActive(index) },
      ]"
    >
      <component :is="headingElement">
        <button
          :ref="!isMobile && firstAccordionActive && index === 0 ? 'firstAccordion' : null"
          type="button"
          class="accordion__trigger"
          :data-for="accordion.title.toLowerCase().replace(' ', '-')"
          :class="[
            $blockClass(blockClass, 'trigger'),
            { [$class('active')]: isActive(index) },
          ]"
          :aria-expanded="isActive(index)"
          @click="toggleAccordion(index)"
        >
          <span
            class="accordion__trigger-label"
            :class="[
              $blockClass(blockClass, 'trigger-label'),
              { [$class('active')]: isActive(index) },
              accordion.titleFontWeightDesktop,
              accordion.titleFontWeightMobile,
            ]"
            v-html="accordion.title"
          />

          <div
            class="accordion__trigger-label-icon"
            :class="{ [$class('active')]: isActive(index) }"
          />
        </button>
      </component>

      <transition-height>
        <div
          v-show="isActive(index)"
          class="accordion__body"
          :class="[
            $blockClass(blockClass, 'body'),
          ]"
        >
          <slot
            :name="`accordion-${index}`"
          >
            <div
              v-if="accordion?.body && accordion?.isRteContent"
            >
              <rte-content
                :class="accordion?.classes || ''"
                :content="accordion.body || ''"
              />
            </div>

            <div
              v-else-if="accordion?.body"
              v-html="accordion?.body"
            />

            <div v-if="accordion.productFitNotes || accordion.productFitGraphic">
              <fit-notes
                :product-fit-notes="accordion?.productFitNotes || null"
                :product-fit-graphic="accordion?.productFitGraphic || null"
              />

              <btn
                type="button"
                modifiers="text inline"
                class-label="text-body-2xs-underline"
                :label="$string('product.accordions.view_size_guide')"
                @click="$emit('open-size-guide')"
              />

              <div
                v-if="accordion?.additionalLine"
                class="fitnotes-additional-text text-body-2xs-regular"
                v-html="accordion?.additionalLine"
              />

            </div>
          </slot>
        </div>
      </transition-height>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Accordion Component (accordion-component)
 * -----------------------------------------------------------------------------
 * Global accordion component
 *
 * @param {Array} accordionData - Array of objects containing title and body.
 * @param {String} blockClass - Block level class to apply to all elements.
 * @param {Boolean} [allowMultiple] - Option to allow multiple open accordions.
 * @param {Boolean} firstAccordionActive - Allows first accordion item open
 */
import Btn from '~global/btn/btn'
import FitNotes from '~async/fit-notes/fit-notes'
import TransitionHeight from '~async/transition-height/transition-height'
import RteContent from '~async/rte-content/rte-content'
import { mapState } from 'vuex'

export default {
  name: 'AccordionSnippet',

  components: {
    Btn,
    FitNotes,
    RteContent,
    TransitionHeight,
  },

  props: {
    accordionData: {
      type: Array,
      default: () => [],
    },
    blockClass: {
      type: String,
      default: '',
    },
    allowMultiple: {
      type: Boolean,
      default: false,
    },
    firstAccordionActive: {
      type: Boolean,
      default: false,
    },
    headingElement: {
      type: String,
      default: 'div',
    },
  },

  emits: {
    'open-size-guide': () => true,
    'accordion-toggle': ({
      index,
      active,
    }) => typeof index === 'number' && typeof active === 'boolean',
  },

  data() {
    return {
      cnvsGlobal: true,
      accordions: [],
      activeIndex: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  watch: {

    /**
     * Updates internal accordion data when prop changes
     * @param {Array|null} newAccordionData - Accordion data from parent
     */
    accordionData: {
      handler(newAccordionData) {
        this.accordions = newAccordionData?.map((accordion, idx) => {
          return {
            active: this.activeIndex === idx ? 1 : 0,
            ...accordion,
          }
        }) || []

      },
      immediate: true,
    },
  },

  mounted() {
    this.$nextTick().then(() => {
      if (!this.isMobile &&
        this.firstAccordionActive &&
        this.$refs.firstAccordion &&
        this.$refs.firstAccordion[0]
      ) {
        this.$refs.firstAccordion[0].click()
      }
    })
  },

  methods: {

    /**
     * Check if an accordion is active using its index.
     * @param {Number} index - Index of a specific accordion.
     * @returns {Boolean}
     */
    isActive(index) {
      return this.accordions[index].active
    },

    /**
     * Toggles the clicked accordion
     * @param {String} accordionIndex - Index of the clicked accordion
     */
    toggleAccordion(accordionIndex) {
      this.activeIndex = accordionIndex
      if (this.allowMultiple) {
        const newIsActive = !this.accordions[accordionIndex].active
        this.accordions[accordionIndex].active = newIsActive

        this.$emit('accordion-toggle', {
          index: accordionIndex,
          active: newIsActive,
        })

        return
      }

      if (this.isActive(accordionIndex)) {
        this.activeIndex = null
        this.accordions[accordionIndex].active = false

        this.$emit('accordion-toggle', {
          index: accordionIndex,
          active: false,
        })

        return
      }

      this.accordions.forEach((panel, index) => {
        panel.active = accordionIndex === index

        this.$emit('accordion-toggle', {
          index,
          active: accordionIndex === index,
        })
      })
    },
  },
}
</script>
